import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaTrashAlt,
  FaArrowRight,
  FaCheckCircle,
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaShieldAlt,
  FaTruck,
  FaGift,
  FaTimes,
  FaPercent,
  FaRupeeSign,
  FaChevronRight,
  FaFire,
  FaTicketAlt,
  FaLock,
  FaBolt,
  FaMinus,
  FaPlus,
  FaEye,
} from "react-icons/fa";
import { MdLocalOffer, MdVerified } from "react-icons/md";
import { RiShoppingBag3Fill } from "react-icons/ri";
import { useApp } from "../App";
import api from "../services/api";
import { BASE_URL } from "../config";
import "../styles/CartPage.css";

export default function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    updateQty,
    cartTotal,
    showToast,
    addToCart,
    fetchCart,
  } = useApp();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [validating, setValidating] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [removingKey, setRemovingKey] = useState(null);
  const [bumpKey, setBumpKey] = useState(null);
  const [couponPanelOpen, setCouponPanelOpen] = useState(false);
  const [pulseTotal, setPulseTotal] = useState(false);

  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [addingProduct, setAddingProduct] = useState(null);

  const FREE_SHIPPING_THRESHOLD = 1000;
  const shipping = cartTotal >= FREE_SHIPPING_THRESHOLD ? 0 : 80;
  const total = cartTotal - discountAmt + shipping;
  const progressPct = Math.min(
    100,
    (cartTotal / FREE_SHIPPING_THRESHOLD) * 100,
  );
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);
  const needsMore = remaining > 0;

  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  useEffect(() => {
    if (needsMore && cart.length > 0) {
      fetchSuggestedProducts();
    } else {
      setSuggestedProducts([]);
    }
  }, [cartTotal, cart.length, needsMore]);

  useEffect(() => {
    setPulseTotal(true);
    const t = setTimeout(() => setPulseTotal(false), 400);
    return () => clearTimeout(t);
  }, [total, discountAmt]);

  const fetchAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const coupons = await api.coupons.getActive();
      setAvailableCoupons(coupons);
    } catch {
      setAvailableCoupons([
        {
          code: "WELCOME10",
          discountType: "percentage",
          discountValue: 10,
          minOrderValue: 500,
          description: "10% off on first order",
        },
        {
          code: "SAVE20",
          discountType: "percentage",
          discountValue: 20,
          minOrderValue: 1000,
          description: "20% off on orders above ₹1000",
        },
        {
          code: "FLAT100",
          discountType: "fixed",
          discountValue: 100,
          minOrderValue: 500,
          description: "Flat ₹100 off",
        },
      ]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const fetchSuggestedProducts = async () => {
    setLoadingSuggestions(true);

    try {
      const cartProductIds = cart.map((item) => item.productId);

      const response = await api.products.getAll();

      let productsArray = [];

      if (response && response.products && Array.isArray(response.products)) {
        productsArray = response.products;
      } else if (Array.isArray(response)) {
        productsArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        productsArray = response.data;
      }

      if (productsArray.length === 0) {
        setSuggestedProducts([]);
        setLoadingSuggestions(false);
        return;
      }

      const maxPrice = remaining < 200 ? remaining + 500 : remaining + 300;

      let filtered = productsArray
        .filter((product) => {
          const productId = product.id || product._id;
          const productPrice = parseFloat(product.price);
          return (
            productId &&
            !cartProductIds.includes(productId) &&
            product.inStock === true &&
            productPrice <= maxPrice
          );
        })
        .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
        .slice(0, 4);

      if (filtered.length < 4) {
        const moreProducts = productsArray
          .filter((product) => {
            const productId = product.id || product._id;
            return (
              productId &&
              !cartProductIds.includes(productId) &&
              !filtered.includes(product) &&
              product.inStock === true
            );
          })
          .slice(0, 4 - filtered.length);
        filtered = [...filtered, ...moreProducts];
      }

      setSuggestedProducts(filtered);
    } catch (error) {
      console.error("Failed to fetch suggestions:", error);
      setSuggestedProducts([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const applyCoupon = async (code = null) => {
    const codeToApply = (code || coupon).trim().toUpperCase();
    if (!codeToApply) return;
    setValidating(true);
    try {
      const data = await api.coupons.validate(codeToApply, cartTotal);
      setDiscount(data.discountValue);
      setDiscountAmt(data.discountAmount);
      setAppliedCouponCode(codeToApply);
      setCouponPanelOpen(false);
      showToast(
        `Coupon "${codeToApply}" applied! You save ₹${data.discountAmount}.`,
      );
      setCoupon("");
    } catch (err) {
      setDiscount(0);
      setDiscountAmt(0);
      setAppliedCouponCode("");
      showToast(err.message || "Invalid coupon code.", "error");
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setDiscount(0);
    setDiscountAmt(0);
    setAppliedCouponCode("");
    showToast("Coupon removed.");
  };

  const handleRemove = (key) => {
    setRemovingKey(key);
    setTimeout(() => {
      removeFromCart(key);
      setRemovingKey(null);
    }, 380);
  };

  const handleQtyChange = (key, newQty) => {
    setBumpKey(key);
    updateQty(key, newQty);
    setTimeout(() => setBumpKey(null), 300);
  };

  const handleAddSuggestedProduct = async (product) => {
    const productId = product.id || product._id;
    setAddingProduct(productId);

    try {
      let sizes = product.sizes;
      if (typeof sizes === "string") {
        try {
          sizes = JSON.parse(sizes);
        } catch (e) {
          sizes = [];
        }
      }

      let selectedSize = "Regular";
      if (sizes && sizes.length > 0) {
        selectedSize = sizes[0].name || sizes[0];
      }

      const productToAdd = {
        id: productId,
        _id: productId,
        name: product.name,
        brand: product.brand,
        price: parseFloat(product.price),
        image: product.image,
        size: selectedSize,
        qty: 1,
        inStock: product.inStock,
        description: product.description,
        category: product.category,
      };

      await addToCart(productToAdd, 1, selectedSize);

      showToast(`${product.name} added to cart!`, "success");

      if (fetchCart) {
        await fetchCart();
      }

      setTimeout(() => {
        fetchSuggestedProducts();
      }, 1000);
    } catch (error) {
      console.error("Failed to add product:", error);
      showToast(error.message || "Failed to add item to cart", "error");
    } finally {
      setTimeout(() => setAddingProduct(null), 500);
    }
  };

  // Empty cart view
  if (cart.length === 0) {
    return (
      <div className="cpx-page cpx-page--empty">
        <div className="cpx-container">
          <div className="cpx-empty-card">
            <div className="cpx-empty-icon-ring">
              <RiShoppingBag3Fill className="cpx-empty-icon" />
            </div>
            <h2 className="cpx-empty-title">Your cart is empty</h2>
            <p className="cpx-empty-sub">
              Discover pieces you'll love and add them to your cart.
            </p>
            <Link className="cpx-cta-btn" to="/collection">
              Shop Now <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cpx-page">
      <div className="cpx-container">
        <nav className="cpx-breadcrumb">
          <Link to="/">Home</Link>
          <FaChevronRight className="cpx-bc-sep" />
          <span className="cpx-bc-active">Cart</span>
        </nav>

        <div className="cpx-layout">
          <div className="cpx-left">
            <div className="cpx-section-head">
              <h1 className="cpx-title">
                Shopping Cart
                <span className="cpx-badge">{cart.length}</span>
              </h1>
            </div>

            <div className="cpx-items">
              {cart.map((item) => (
                <div
                  key={item.key}
                  className={`cpx-item ${removingKey === item.key ? "cpx-item--removing" : "cpx-item--visible"}`}
                >
                  <div
                    className="cpx-item-img"
                    onClick={() => navigate(`/product/${item.productId}`)}
                  >
                    <img
                      src={
                        item.image?.startsWith("http")
                          ? item.image
                          : `${BASE_URL}${item.image}`
                      }
                      alt={item.name}
                      onError={(e) => {
                        e.target.src = `https://placehold.co/100x130/f0e8dc/8B1A1A?text=${item.name?.charAt(0) || "P"}`;
                      }}
                    />
                    <div className="cpx-item-img-overlay">
                      <FaArrowRight />
                    </div>
                  </div>

                  <div className="cpx-item-body">
                    <div className="cpx-item-top">
                      <div>
                        <p className="cpx-item-brand">{item.brand}</p>
                        <h3
                          className="cpx-item-name"
                          onClick={() => navigate(`/product/${item.productId}`)}
                        >
                          {item.name}
                        </h3>
                        <div className="cpx-item-tags">
                          <span className="cpx-item-tag">
                            Size: {item.size}
                          </span>
                          {item.color && (
                            <span className="cpx-item-tag">
                              Color: {item.color}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="cpx-item-price">
                        ₹{(item.price * item.qty).toLocaleString()}
                      </div>
                    </div>

                    <div className="cpx-item-foot">
                      <div className="cpx-qty">
                        <button
                          className="cpx-qty-btn"
                          onClick={() =>
                            handleQtyChange(item.key, item.qty - 1)
                          }
                          disabled={item.qty <= 1}
                        >
                          <FaMinus />
                        </button>
                        <span
                          className={`cpx-qty-val ${bumpKey === item.key ? "cpx-qty-bump" : ""}`}
                        >
                          {item.qty}
                        </span>
                        <button
                          className="cpx-qty-btn"
                          onClick={() =>
                            handleQtyChange(item.key, item.qty + 1)
                          }
                        >
                          <FaPlus />
                        </button>
                      </div>
                      <span className="cpx-unit-price">
                        ₹{item.price.toLocaleString()} each
                      </span>
                      <button
                        className="cpx-remove-btn"
                        onClick={() => handleRemove(item.key)}
                        aria-label="Remove"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="cpx-trust-row">
              <div className="cpx-trust-item">
                <FaTruck className="cpx-trust-icon" />
                <span>Fast Delivery</span>
              </div>
              <div className="cpx-trust-item">
                <FaShieldAlt className="cpx-trust-icon" />
                <span>Secure Payments</span>
              </div>
              <div className="cpx-trust-item">
                <MdVerified className="cpx-trust-icon" />
                <span>100% Authentic</span>
              </div>
              <div className="cpx-trust-item">
                <FaGift className="cpx-trust-icon" />
                <span>Gift Wrapping</span>
              </div>
            </div>

            {/* SUGGESTED PRODUCTS SECTION */}
            {needsMore && (
              <div className="cpx-suggestions">
                <div className="cpx-suggestions-header">
                  <FaFire className="cpx-suggestions-icon" />
                  <div>
                    <h3 className="cpx-suggestions-title">
                      Add ₹{remaining.toLocaleString()} more for FREE Shipping!
                    </h3>
                    <p className="cpx-suggestions-sub">
                      You're almost there. Add these items to unlock free
                      delivery.
                    </p>
                  </div>
                </div>

                {loadingSuggestions ? (
                  <div className="cpx-suggestions-loading">
                    <div className="cpx-suggestions-loader"></div>
                    <span>Finding perfect picks for you...</span>
                  </div>
                ) : suggestedProducts.length > 0 ? (
                  <>
                    <div className="cpx-suggestions-grid">
                      {suggestedProducts.map((product) => {
                        const productId = product.id || product._id;
                        const productPrice = parseFloat(product.price);
                        const fillPercentage = Math.min(
                          100,
                          Math.ceil((productPrice / remaining) * 100),
                        );

                        return (
                          <div key={productId} className="cpx-suggestion-card">
                            <div className="cpx-suggestion-img">
                              <img
                                src={
                                  product.image?.startsWith("http")
                                    ? product.image
                                    : `${BASE_URL}${product.image}`
                                }
                                alt={product.name}
                                onError={(e) => {
                                  e.target.src = `https://placehold.co/200x200/f0e8dc/8B1A1A?text=${product.name?.charAt(0) || "P"}`;
                                }}
                              />
                              {productPrice <= remaining && remaining > 0 && (
                                <div className="cpx-suggestion-badge">
                                  Fills {fillPercentage}%
                                </div>
                              )}
                            </div>
                            <div className="cpx-suggestion-info">
                              <p className="cpx-suggestion-brand">
                                {product.brand || "KKB & Co."}
                              </p>
                              <p className="cpx-suggestion-name">
                                {product.name}
                              </p>
                              <div className="cpx-suggestion-price-row">
                                <span className="cpx-suggestion-price">
                                  ₹{productPrice.toLocaleString()}
                                </span>
                              </div>
                              <button
                                className="cpx-suggestion-add"
                                onClick={() =>
                                  handleAddSuggestedProduct(product)
                                }
                                disabled={addingProduct === productId}
                              >
                                {addingProduct === productId ? (
                                  <span className="cpx-spinner" />
                                ) : (
                                  <>
                                    <FaPlus /> Quick Add
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      className="cpx-view-all-btn"
                      onClick={() => navigate("/collection")}
                    >
                      <FaEye /> Browse All Products
                      <FaChevronRight />
                    </button>
                  </>
                ) : (
                  <div className="cpx-suggestions-empty">
                    <p>No additional products found.</p>
                    <button
                      className="cpx-view-all-btn"
                      onClick={() => navigate("/collection")}
                    >
                      <FaEye /> Browse All Products
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="cpx-right">
            <div className="cpx-summary-card">
              <h3 className="cpx-summary-title">Order Summary</h3>

              <div className="cpx-ship-box">
                <div className="cpx-ship-top">
                  <FaTruck
                    className={`cpx-ship-icon ${remaining === 0 ? "cpx-ship-icon--free" : ""}`}
                  />
                  {remaining > 0 ? (
                    <p className="cpx-ship-text">
                      Add <strong>₹{remaining.toLocaleString()}</strong> more
                      for <strong>FREE shipping</strong>
                    </p>
                  ) : (
                    <p className="cpx-ship-free">
                      <FaCheckCircle /> You've unlocked FREE shipping!
                    </p>
                  )}
                </div>
                <div className="cpx-progress-track">
                  <div
                    className="cpx-progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="cpx-progress-labels">
                  <span>₹0</span>
                  <span>₹{FREE_SHIPPING_THRESHOLD.toLocaleString()}</span>
                </div>
              </div>

              <div className="cpx-coupon-wrap">
                {appliedCouponCode ? (
                  <div className="cpx-applied-coupon">
                    <div className="cpx-applied-left">
                      <FaTicketAlt className="cpx-applied-icon" />
                      <div>
                        <span className="cpx-applied-code">
                          {appliedCouponCode}
                        </span>
                        <span className="cpx-applied-save">
                          Saving ₹{discountAmt.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button
                      className="cpx-remove-coupon"
                      onClick={removeCoupon}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cpx-coupon-row">
                      <div className="cpx-coupon-field">
                        <MdLocalOffer className="cpx-coupon-ico" />
                        <input
                          className="cpx-coupon-input"
                          type="text"
                          placeholder="Enter coupon code"
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                        />
                      </div>
                      <button
                        className="cpx-apply-btn"
                        onClick={() => applyCoupon()}
                        disabled={validating || !coupon.trim()}
                      >
                        {validating ? (
                          <span className="cpx-spinner" />
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>

                    <button
                      className="cpx-offers-toggle"
                      onClick={() => setCouponPanelOpen((p) => !p)}
                    >
                      <FaBolt className="cpx-offers-ico" />
                      {availableCoupons.length} offers available
                      <FaChevronRight
                        className={`cpx-offers-arrow ${couponPanelOpen ? "cpx-offers-arrow--open" : ""}`}
                      />
                    </button>

                    {couponPanelOpen && (
                      <div className="cpx-offers-panel">
                        {loadingCoupons ? (
                          <div className="cpx-offers-loading">
                            <span className="cpx-spinner cpx-spinner--dark" />{" "}
                            Loading offers...
                          </div>
                        ) : (
                          availableCoupons.map((c, i) => (
                            <div
                              key={i}
                              className="cpx-offer-card"
                              onClick={() => applyCoupon(c.code)}
                            >
                              <div className="cpx-offer-left">
                                <span className="cpx-offer-code">{c.code}</span>
                                <span className="cpx-offer-desc">
                                  {c.description}
                                </span>
                                {c.minOrderValue > 0 && (
                                  <span className="cpx-offer-min">
                                    Min. ₹{c.minOrderValue}
                                  </span>
                                )}
                              </div>
                              <div className="cpx-offer-right">
                                <span className="cpx-offer-discount">
                                  {c.discountType === "percentage" ? (
                                    <>
                                      <FaPercent /> {c.discountValue}% OFF
                                    </>
                                  ) : (
                                    <>
                                      <FaRupeeSign /> {c.discountValue} OFF
                                    </>
                                  )}
                                </span>
                                <button className="cpx-offer-apply">
                                  Apply →
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="cpx-divider" />

              <div className="cpx-price-rows">
                <div className="cpx-price-row">
                  <span>Subtotal ({cart.length} items)</span>
                  <span>₹{cartTotal.toLocaleString()}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="cpx-price-row cpx-price-row--discount">
                    <span>
                      <FaPercent style={{ fontSize: 10, marginRight: 4 }} />{" "}
                      Coupon Discount
                    </span>
                    <span>−₹{discountAmt.toLocaleString()}</span>
                  </div>
                )}
                <div className="cpx-price-row">
                  <span>Shipping</span>
                  <span>
                    {shipping === 0 ? (
                      <span className="cpx-free-chip">FREE</span>
                    ) : (
                      `₹${shipping}`
                    )}
                  </span>
                </div>
              </div>

              <div className="cpx-divider" />

              <div
                className={`cpx-total-row ${pulseTotal ? "cpx-total-row--pulse" : ""}`}
              >
                <span>Total</span>
                <span>₹{total.toLocaleString()}</span>
              </div>

              {discountAmt > 0 && (
                <div className="cpx-savings-strip">
                  <FaFire /> You're saving ₹{discountAmt.toLocaleString()} on
                  this order!
                </div>
              )}

              <button
                className="cpx-checkout-btn"
                onClick={() =>
                  navigate("/checkout", {
                    state: {
                      discountAmt,
                      couponCode: appliedCouponCode,
                      discount,
                    },
                  })
                }
              >
                <FaLock style={{ fontSize: 14 }} />
                Proceed to Checkout
                <FaArrowRight style={{ fontSize: 14 }} />
              </button>

              <p className="cpx-secure-note">
                <FaShieldAlt /> 256-bit SSL encrypted & secure
              </p>

              <div className="cpx-payments">
                <FaCcVisa title="Visa" />
                <FaCcMastercard title="Mastercard" />
                <FaCcAmex title="Amex" />
                <span className="cpx-upi-badge">UPI</span>
                <span className="cpx-upi-badge">COD</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
